import type { ApiClient } from '@carbonwebmail/api-client'
import type {
  CampaignContentDto,
  CampaignPreviewResponse,
  SegmentListDto,
} from '@carbonwebmail/contracts'
import { proseBody } from '@carbonwebmail/domain'
import { useRef } from 'react'
import { Link, useParams } from 'react-router'
import { Alert } from '../../ui/Alert.js'
import type { AsyncState } from '../../ui/Async.js'
import { Async } from '../../ui/Async.js'
import { audienceOptions } from '../../ui/audienceOptions.js'
import { BodyEditor } from '../../ui/BodyEditor.js'
import { Field, SelectField } from '../../ui/Field.js'
import { MessagePreview } from '../../ui/MessagePreview.js'
import type { Draft, SaveState } from './useComposer.js'
import { useComposer } from './useComposer.js'

/**
 * Writing a campaign, with the message beside it.
 *
 * Its own full-width layout rather than the ordinary page: the editor and the
 * preview are the screen, and a sidebar competing with them helps nobody. The
 * way back is a link, not a browser button.
 *
 * Everything editable on a draft is here, not only the message. A composer that
 * could fix a typo in the body but not in the From address would send that typo
 * to forty thousand people.
 *
 * There are no drag-and-drop blocks. A block editor changes what a campaign
 * *is* — from prose to a structured document — which changes the schema, the
 * renderer and every template written so far. That is a decision to take
 * deliberately rather than one to arrive at by building an editor.
 */
export function ComposerPage({ client }: { readonly client: ApiClient }) {
  const { campaignId = '' } = useParams()
  const composer = useComposer({ client, campaignId })

  return (
    <Async
      state={composer.state}
      skeleton={<p className="text-[13px] text-t3">Opening…</p>}
      onRetry={() => window.location.reload()}
    >
      {(campaign) => (
        <Editor
          campaign={campaign}
          segments={composer.segments}
          draft={composer.draft}
          preview={composer.preview}
          save={composer.save}
          onChange={composer.change}
          onCommit={() => void composer.commit()}
        />
      )}
    </Async>
  )
}

/**
 * What the screen has to say about the campaign and the last save.
 *
 * The two that are not obvious are both about a send already running. A queued
 * campaign is not editable, and saying *why* matters more than hiding the
 * fields — changing it now would mean two different emails went out under one
 * campaign id. And a campaign queued *while somebody was writing* needs telling
 * that nothing was saved and that what is going out is unchanged, rather than a
 * generic failure that leaves them wondering which.
 */
function Notices({ editable, save }: { readonly editable: boolean; readonly save: SaveState }) {
  if (!editable) {
    return (
      <Alert>
        This campaign has been queued, so it can no longer be changed. Everything below is what was
        sent.
      </Alert>
    )
  }

  switch (save.status) {
    case 'not-a-draft':
      return (
        <Alert>
          This campaign was queued while you were writing, so nothing was saved. What is already
          going out is unchanged.
        </Alert>
      )
    case 'failed':
      return <Alert>{save.detail}</Alert>
    case 'saved':
      return <Alert tone="ok">Saved.</Alert>
    default:
      return null
  }
}

function Editor({
  campaign,
  segments,
  draft,
  preview,
  save,
  onChange,
  onCommit,
}: {
  readonly campaign: CampaignContentDto
  readonly segments: AsyncState<SegmentListDto>
  readonly draft: Draft | undefined
  readonly preview: CampaignPreviewResponse | undefined
  readonly save: SaveState
  readonly onChange: <K extends keyof Draft>(key: K, value: Draft[K]) => void
  readonly onCommit: () => void
}) {
  const _body = useRef<HTMLTextAreaElement>(null)
  const editable = campaign.status === 'draft'
  const locked = !editable || draft === undefined

  const audiences = audienceOptions(segments)

  return (
    <div className="mx-auto max-w-[1400px]">
      <header className="mb-5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <Link className="font-medium text-[13px] text-t3 hover:text-t1" to="/campaigns">
            ← Campaigns
          </Link>
          <h1 className="mt-1 font-bold text-[20px] text-t1 -tracking-[0.01em]">{campaign.name}</h1>
        </div>
        {editable ? (
          <button
            type="button"
            disabled={save.status === 'saving' || draft === undefined}
            onClick={onCommit}
            className="rounded-pill bg-brand px-5 py-2.5 font-bold text-[14px] text-brand-contrast disabled:cursor-not-allowed disabled:opacity-60"
          >
            {save.status === 'saving' ? 'Saving…' : 'Save draft'}
          </button>
        ) : null}
      </header>

      <Notices editable={editable} save={save} />

      <div className="grid gap-5 lg:grid-cols-2">
        <section className="rounded-card border border-line bg-surface p-5">
          <Field
            id="composer-name"
            label="Campaign name"
            type="text"
            autoComplete="off"
            value={draft?.name ?? ''}
            disabled={locked}
            hint="Only you see this."
            onChange={(value) => onChange('name', value)}
          />

          <SelectField
            id="composer-audience"
            label="Audience"
            value={draft?.segmentId ?? ''}
            disabled={locked || segments.status === 'loading'}
            options={audiences}
            hint="Worked out when you send, not now. Anyone who unsubscribes before then is left out."
            onChange={(value) => onChange('segmentId', value)}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              id="composer-from-name"
              label="From name"
              type="text"
              autoComplete="off"
              value={draft?.fromName ?? ''}
              disabled={locked}
              onChange={(value) => onChange('fromName', value)}
            />
            <Field
              id="composer-from-email"
              label="From address"
              type="email"
              autoComplete="off"
              value={draft?.fromEmail ?? ''}
              disabled={locked}
              onChange={(value) => onChange('fromEmail', value)}
            />
          </div>

          <Field
            id="composer-reply-to"
            optional
            label="Reply-to address"
            type="email"
            autoComplete="off"
            value={draft?.replyTo ?? ''}
            disabled={locked}
            hint="Where a reply goes. Left empty, replies go to the from address."
            onChange={(value) => onChange('replyTo', value)}
          />

          <Field
            id="composer-subject"
            label="Subject"
            type="text"
            autoComplete="off"
            value={draft?.subject ?? ''}
            disabled={locked}
            onChange={(value) => onChange('subject', value)}
          />

          <BodyEditor
            id="composer-body"
            body={draft?.body ?? proseBody('')}
            disabled={locked}
            composeAt={`/compose/campaign/${campaign.campaignId}`}
            onChange={(value) => onChange('body', value)}
          />

          {/*
            A campaign written before the source was stored has a rendered
            message and nothing that produced it. Saying so beats opening an
            empty editor over content somebody cannot see.
          */}
          {campaign.body === null ? (
            <p className="mt-4 rounded-field border border-line bg-surface-2 px-3 py-2.5 text-[12px] text-t2">
              This campaign was written before its text was kept, so the editor starts empty. Saving
              will replace the message entirely.
            </p>
          ) : null}
        </section>

        <section className="rounded-card border border-line bg-surface p-5">
          <MessagePreview preview={preview} />
        </section>
      </div>
    </div>
  )
}
